import React, { useEffect, useRef, useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  LinearProgress,
  Paper
} from '@mui/material';
import { CloudUpload, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as meetingService from '../../services/meetingService';

const PENDING = 'pending';
const UPLOADING = 'uploading';
const SUCCESS = 'success';
const FAILED = 'failed';

const UploadMeeting = () => {
  const [description, setDescription] = useState('');
  const [speakerAnchors, setSpeakerAnchors] = useState('');
  const [uploadRows, setUploadRows] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const isUploading = uploadRows.some((row) => row.status === UPLOADING);
  const hasFailedUploads = uploadRows.some((row) => row.status === FAILED);
  const eligibleRows = uploadRows.filter(
    (row) => row.status === PENDING || row.status === FAILED
  );

  useEffect(() => {
    document.title = 'Upload Meeting - EchoNote';
  }, []);

  const updateRow = (rowId, changes) => {
    setUploadRows((currentRows) =>
      currentRows.map((row) => row.id === rowId ? { ...row, ...changes } : row)
    );
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.currentTarget.files);
    const rows = selectedFiles.map((file, index) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${index}`,
      file,
      title: file.name.replace(/\.[^.]+$/, '') || file.name,
      status: PENDING,
      progress: null,
      error: ''
    }));

    setUploadRows(rows);
    setError('');
    setSuccess('');
  };

  const handleTitleChange = (rowId, title) => {
    updateRow(rowId, { title, error: '' });
    setError('');
  };

  const uploadRow = async (row) => {
    const formData = new FormData();
    formData.append('file', row.file);
    formData.append('title', row.title.trim());
    formData.append('description', description);
    if (speakerAnchors.trim()) formData.append('speaker_anchors', speakerAnchors.trim());

    try {
      await meetingService.uploadMeeting(formData, (progressEvent) => {
        const { loaded, total } = progressEvent;
        const progress = Number.isFinite(total) && total > 0
          ? Math.min(100, Math.max(0, Math.round((loaded * 100) / total)))
          : null;

        updateRow(row.id, { progress });
      });
      updateRow(row.id, { status: SUCCESS, progress: 100, error: '' });
    } catch (uploadError) {
      const message = uploadError.response?.data?.error
        || uploadError.message
        || 'Upload failed';
      updateRow(row.id, { status: FAILED, progress: null, error: message });
      throw uploadError;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (eligibleRows.length === 0) {
      if (uploadRows.length === 0) {
        setError('Please select at least one file to upload');
      }
      return;
    }

    if (eligibleRows.some((row) => !row.title.trim())) {
      setError('A title is required for every file');
      return;
    }

    const eligibleIds = new Set(eligibleRows.map((row) => row.id));
    setError('');
    setSuccess('');
    setUploadRows((currentRows) =>
      currentRows.map((row) => eligibleIds.has(row.id)
        ? { ...row, status: UPLOADING, progress: null, error: '' }
        : row)
    );

    const results = await Promise.allSettled(eligibleRows.map(uploadRow));
    const allEligibleSucceeded = results.every((result) => result.status === 'fulfilled');
    const allRowsSucceeded = allEligibleSucceeded && uploadRows.every(
      (row) => eligibleIds.has(row.id) || row.status === SUCCESS
    );

    if (!allRowsSucceeded) {
      setError('Some files could not be uploaded. Review the errors below and retry failed uploads.');
      return;
    }

    setSuccess('Meeting uploaded successfully! Processing will begin shortly.');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setTimeout(() => navigate('/dashboard'), 2000);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusText = (row) => {
    if (row.status === UPLOADING) {
      return row.progress === null ? 'Uploading...' : `Uploading... ${row.progress}%`;
    }
    if (row.status === SUCCESS) return 'Upload successful';
    if (row.status === FAILED) return `Upload failed: ${row.error}`;
    return 'Pending upload';
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          sx={{ mr: 2 }}
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4" component="h1">
          Upload Meetings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Description (optional)"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              margin="normal"
              multiline
              rows={3}
              disabled={isUploading}
            />

            <TextField
              fullWidth
              label="Speaker identity anchors (optional JSON)"
              value={speakerAnchors}
              onChange={(event) => setSpeakerAnchors(event.target.value)}
              margin="normal"
              multiline
              rows={4}
              disabled={isUploading}
              helperText={'Example: {"participants":[{"name":"Brian","anchors":[{"start":6,"end":9}]}]}'}
            />

            <Box sx={{ mt: 3, mb: 2 }}>
              <input
                ref={fileInputRef}
                accept="audio/*,video/*"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                multiple
                onChange={handleFileChange}
                disabled={isUploading}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  disabled={isUploading}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {uploadRows.length > 0 ? 'Change Files' : 'Select Audio/Video Files'}
                </Button>
              </label>
            </Box>

            {uploadRows.map((row) => (
              <Paper key={row.id} elevation={1} sx={{ p: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label={`Meeting title for ${row.file.name}`}
                  value={row.title}
                  onChange={(event) => handleTitleChange(row.id, event.target.value)}
                  margin="normal"
                  required
                  disabled={isUploading || row.status === SUCCESS}
                />
                <Typography variant="body2" color="textSecondary">
                  Selected File:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {row.file.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Size: {formatFileSize(row.file.size)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Type: {row.file.type || 'Unknown'}
                </Typography>
                <Typography
                  variant="body2"
                  color={row.status === FAILED ? 'error' : 'textSecondary'}
                  sx={{ mt: 1 }}
                >
                  Status: {getStatusText(row)}
                </Typography>
                {row.status === UPLOADING && (
                  <LinearProgress
                    aria-label={`Upload progress for ${row.file.name}`}
                    variant={row.progress === null ? 'indeterminate' : 'determinate'}
                    value={row.progress === null ? undefined : row.progress}
                    sx={{ mt: 1 }}
                  />
                )}
              </Paper>
            ))}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isUploading || eligibleRows.length === 0}
              sx={{ mt: 2, py: 1.5 }}
            >
              {isUploading
                ? 'Uploading...'
                : hasFailedUploads
                  ? 'Retry Failed Uploads'
                  : uploadRows.length > 1
                    ? 'Upload Meetings'
                    : 'Upload Meeting'}
            </Button>
          </Box>

          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Supported file formats:
            </Typography>
            <Typography variant="body2">
              Audio: MP3, WAV, M4A
            </Typography>
            <Typography variant="body2">
              Video: MP4, AVI, MOV
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Maximum file size: 500MB per file
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UploadMeeting;
